/**
 * auth.store.ts — Zustand Auth UI State Store
 *
 * Manages the client-side representation of auth state:
 *   - Whether Firebase is still determining the initial auth state (isLoading)
 *   - The current user (or null if logged out)
 *   - The current auth error (or null)
 *
 * Architecture note:
 *   - This store holds UI state — it is NOT the source of truth for the token.
 *     Tokens come from Firebase Auth directly via authService.getToken().
 *   - The store is populated by the useAuth hook which listens to
 *     authService.onAuthStateChanged().
 *   - Components subscribe to this store for reactive auth state; they do NOT
 *     call authService directly.
 *
 * Usage:
 *   const { user, isLoading } = useAuthStore();
 */
import { create } from 'zustand';
import type { User } from 'firebase/auth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthState {
  /** Firebase User object, or null when not authenticated. */
  user: User | null;
  /**
   * True while Firebase is asynchronously determining the initial auth state.
   * Components should show a loading screen (not the login page) during this
   * window to prevent a flash of the unauthenticated UI.
   */
  isLoading: boolean;
  /** Last auth error, or null if no error. Cleared on successful auth. */
  error: Error | null;
}

interface AuthActions {
  /** Called by useAuth hook when Firebase resolves the auth state. */
  setUser: (user: User | null) => void;
  /** Called by useAuth hook when an auth operation fails. */
  setError: (error: Error | null) => void;
  /** Called by useAuth hook when the initial Firebase auth check completes. */
  setLoading: (isLoading: boolean) => void;
  /**
   * Updates only the current user's avatar (photoURL), leaving the rest of the
   * user object intact. Used by the header ProfileMenu's demo avatar upload so
   * the new avatar reflects everywhere the store is read. No-op when logged out.
   */
  setAvatarUrl: (photoURL: string) => void;
  /** Resets the entire store to its initial state (e.g., on sign-out). */
  reset: () => void;
}

export type AuthStore = AuthState & AuthActions;

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: AuthState = {
  user: null,
  isLoading: true, // Optimistic: assume loading until Firebase says otherwise
  error: null,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAuthStore = create<AuthStore>()((set) => ({
  ...initialState,

  setUser: (user) =>
    set({
      user,
      isLoading: false,
      error: null,
    }),

  setError: (error) =>
    set({
      error,
      isLoading: false,
    }),

  setLoading: (isLoading) => set({ isLoading }),

  setAvatarUrl: (photoURL) =>
    set((state) =>
      state.user !== null ? { user: { ...state.user, photoURL } as User } : {},
    ),

  reset: () => set(initialState),
}));
